/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — shared types. Single source of truth for the props
   contract (mirrors the OLD `OrgChart` component's exported props
   interface verbatim — People Rebuild §3 requires this be a drop-in
   swap), plus the RF node/edge `data` shapes used across the split
   files (nodes, edges, hooks, menus).
   ═══════════════════════════════════════════════════════════════ */

import type {
  TeamMember,
  Team,
  TeamAssignment,
  Project,
  Role,
  BillingRole,
} from '../../../../shared/types';
import type {
  Department,
  OrgChartEdge,
  OrgChartJunction,
  OrgChartNote,
  OrgChartView,
  OrgChartViewPosition,
} from '../../../../shared/entities';
import type { HopsBySegment } from './edgeGeometry';

/** Line style — per-edge user pick. */
export type OrgEdgeLineStyle = 'solid' | 'dashed' | 'dotted';

/** Position-only update — used by the (now purely local, in-session)
 *  drag/nudge/align/distribute paths. Moved here from the deleted
 *  `batchedPositionSave.ts` (People Rebuild edit-session grant,
 *  2026-08-17: position writes are no longer their own network call —
 *  they land in the one `commitOrgChart` transaction on Save). */
export type PositionUpdate = { id: string; orgX: number; orgY: number };

/** Parse an RF composite node id (`team-box-<id>` / `junction-<id>` /
 *  a raw member id) back into `{id, type}`. Shared by the initial
 *  edge derivation, the edit-session commit builder, and every
 *  interactive edge handler so all three agree on the same encoding. */
export function parseEndpoint(id: string): { id: string; type: 'member' | 'team' | 'junction' } {
  if (id.startsWith('team-box-')) return { id: id.slice('team-box-'.length), type: 'team' };
  if (id.startsWith('junction-')) return { id: id.slice('junction-'.length), type: 'junction' };
  return { id, type: 'member' };
}

/**
 * Props contract — EXACT mirror of the old `OrgChartProps`
 * (src/renderer/pages/team/orgchart/OrgChart.tsx) so OrgChartV2 is a
 * drop-in swap for the shell. Only ADDITIVE optional props beyond the
 * original are permitted (People Rebuild contract §"Component
 * contract"). `projects`/`roles` are accepted for interface parity
 * even though this surface doesn't render them directly today.
 *
 * EDIT-SESSION MODEL (People Rebuild §0/§3, Josh grant 2026-08-17):
 * every per-field network write the old contract exposed
 * (onSavePositions / onReportsToChange / onCreateEdge / onUpdateEdge /
 * onDeleteEdge / onCreateJunction / onUpdateJunction / onDeleteJunction
 * / onAddToChart) is GONE. The chart now accumulates all of that
 * locally during an edit session and lands it in ONE
 * `commitOrgChart()` transaction on Save (see OrgChartV2.tsx). The
 * only two write paths that remain immediate are `onRemoveFromChart`
 * (its own destructive confirm, also deletes team_assignments — not
 * part of the commit payload) and the team-membership handlers
 * (`onTeamAssign`/`onTeamRemove`/`onToggleTeamRole`), which write to
 * the unrelated team_assignments table and were never part of the
 * stale-position/stale-row defect class this model fixes.
 * `onInsertBetween`/`onMakeParentOf` are REMOVED — they were already
 * unreachable dead code (no menu/UI ever called them; grep-verified
 * pre-change).
 */
export interface OrgChartV2Props {
  members: TeamMember[];
  teams: Team[];
  teamAssignments: TeamAssignment[];
  departments: Department[];
  roles: Role[];
  billingRoles: BillingRole[];
  orgChartEdges: OrgChartEdge[];
  orgChartJunctions: OrgChartJunction[];
  /** Free-floating text-box notes (migration 040, Josh grant
   *  2026-08-17). OPTIONAL — omitted callers just don't render any
   *  notes (fail-closed, same pattern as canViewBilling/canViewRoles),
   *  keeping this a drop-in-safe addition. */
  orgChartNotes?: OrgChartNote[];
  roleName: (m: TeamMember) => string;
  onMemberClick: (m: TeamMember) => void;
  onTeamAssign: (memberId: string, teamId: string) => void;
  onTeamRemove: (memberId: string, teamId: string) => void;
  onToggleTeamRole: (memberId: string, teamId: string) => void;
  /** The ONE immediate-write exception — see contract note above.
   *  Only reachable while an edit session is active. */
  onRemoveFromChart: (memberId: string) => void;
  onDeleteMember: (memberId: string) => void;
  /** When false the chart is view-only: pan/zoom/click-through to a
   *  member detail, but no drags/connections/edits/deletions. Gated
   *  on the `team_members.edit` permission by the shell. This is the
   *  PERMISSION gate — whether the "Edit chart" button shows at all.
   *  Whether editing affordances are actually live right now is
   *  `canEdit && <session active>`, computed inside OrgChartV2. */
  canEdit: boolean;
  /** ── Additive (People Rebuild §3/§7) ──────────────────────────
   *  Permission flags for the hover profile-card field gating.
   *  Optional — when omitted the gated fields simply don't render
   *  (fail-closed), matching the shell's existing flag-omission
   *  behavior elsewhere in the app. */
  canViewBilling?: boolean;
  canViewRoles?: boolean;
  /** Unused `projects` kept for interface-parity callers that pass
   *  it through from the shell; org chart v2 doesn't render it. */
  projects?: Project[];
  /** Called once after a session commit lands (success) and after a
   *  saved-view create/update/delete — the shell's `loadAll()`, so
   *  `members`/`orgChartEdges`/`orgChartJunctions`/`orgChartViews`
   *  reflect the server truth. This is the sender's own refresh path
   *  that closes defect #1 (stale positions forever). */
  refreshAll: () => Promise<void>;
  /** Toast surface for commit/save-view success + failure copy. */
  toast: (msg: string, isError?: boolean) => void;
  /** Saved org-chart views — already visibility-filtered server-side. */
  orgChartViews: OrgChartView[];
  /** Per-view member placements for every CUSTOM board (migration 041,
   *  Josh grant 2026-08-17: "views become separate canvases/boards").
   *  Main's placements stay on `members[].orgX/orgY` — this is only
   *  consulted when the active view id is non-null. Optional/defaulted
   *  empty so existing callers that haven't wired the new load stay a
   *  drop-in-safe build (they simply can't open a non-Main board yet). */
  orgChartViewPositions?: OrgChartViewPosition[];
  /** Signed-in user's team_members.id — view ownership + `createdBy`
   *  stamping on create. */
  currentTeamMemberId?: string;
  /** admin_center.manage_roles (or site_admin) — lets the picker show
   *  edit/delete affordances on views this user didn't create; the
   *  server enforces the real rule regardless. */
  isViewAdmin?: boolean;
  /** Fires whenever "does this chart have an active edit session with
   *  unsaved changes" changes (2026-08-19 no-work-loss hardening) — the
   *  shell (PeoplePage) mirrors this into a ref so it can block a
   *  Directory/Org-Chart tab switch (or any other action that would
   *  unmount this component) while a dirty session is open, instead of
   *  silently discarding it. Optional — omitted callers get the old
   *  unguarded behavior (fail-open, matches every other optional prop
   *  here), never a build break. */
  onDirtyChange?: (dirty: boolean) => void;
}

export interface OrgEdgeData extends Record<string, unknown> {
  edgeId?: string;
  waypoints?: { x: number; y: number }[];
  /** Render-only obstacle-avoiding polyline (People Rebuild, Josh
   *  grant 2026-08-17): computed live in OrgChartV2 whenever
   *  `waypoints` is empty on an orthogonal-routing edge. NEVER
   *  persisted — `edgeFromRF`/the commit payload read `waypoints`
   *  only, so this can't leak into the DB. Dragging a segment
   *  converts the edge to manual by writing the shaped result into
   *  `waypoints` (via `onWaypointsChange`), same as it always has. */
  autoWaypoints?: { x: number; y: number }[];
  lineStyle?: OrgEdgeLineStyle;
  color?: string;
  label?: string;
  onWaypointsChange?: (edgeId: string, next: { x: number; y: number }[]) => void;
  sourceMemberId?: string;
  targetMemberId?: string;
  canEdit?: boolean;
  hopsBySeg?: HopsBySegment;
  highlight?: 'active' | 'dimmed' | 'none';
  flashing?: boolean;
  onHoverChange?: (edgeId: string, hovering: boolean) => void;
  onEndpointClick?: (edgeId: string) => void;
}

/** Node customization prefs (People Rebuild §3 "Node customization
 *  [granted]") — persisted per-user like the other org chart prefs
 *  (localStorage seed + `user_prefs.orgchart_nodeStyle`). */
export interface NodeStylePrefs {
  shape: 'rectangle' | 'circle';
  density: 'expanded' | 'compact';
  showDepartmentPill: boolean;
  showTeamPills: boolean;
  showTitleBadge: boolean;
  showLeaderStar: boolean;
}

export const DEFAULT_NODE_STYLE: NodeStylePrefs = {
  shape: 'rectangle',
  density: 'expanded',
  showDepartmentPill: true,
  showTeamPills: true,
  showTitleBadge: true,
  showLeaderStar: true,
};

export type PersonNodeData = {
  member: TeamMember;
  teamColor: string;
  pills: {
    label: string;
    color: string;
    kind: 'team' | 'department';
    /** Department's full name, when `label` is a shortened
     *  abbreviation — the legend (Josh grant 2026-08-18) reads this to
     *  explain the abbreviation; the card itself only ever renders
     *  `label`. Unset for team pills (already the team's full name). */
    fullLabel?: string;
  }[];
  isLeader: boolean;
  companyBadge?: {
    code: string;
    color: string;
    /** Full Title name (e.g. "Managing Principal" for code "MP") — the
     *  legend (Josh grant 2026-08-18) reads this to explain the
     *  abbreviation; the card itself only ever renders `code`. */
    name?: string;
  };
  glow?: boolean;
  nodeStyle: NodeStylePrefs;
  /** Passed through so the node can open the hover profile card with
   *  the fields the viewing user is permitted to see. */
  canViewBilling?: boolean;
  canViewRoles?: boolean;
  roleLabel: string;
  /** Real team memberships (with PER-TEAM leader status) for the
   *  hover profile card — distinct from `pills`, which only carries
   *  label/color and a single chart-wide `isLeader` flag. */
  teamMemberships: { team: Team; isLeader: boolean }[];
};

/** RF node `data` shape for a `textBox` note node. `editing`/`canEdit`/
 *  `onCommitText`/`onCancelEdit` are injected LIVE by an effect in
 *  OrgChartV2 (same "inject callbacks into node.data" pattern the org
 *  edges use) — they change mid-session (e.g. double-click enters
 *  edit) without triggering the session-guarded props resync. */
export type NoteNodeData = {
  text: string;
  fontSize: number;
  color?: string;
  width?: number;
  editing?: boolean;
  canEdit?: boolean;
  onCommitText?: (id: string, text: string) => void;
  onCancelEdit?: (id: string) => void;
};

// Layout-estimate fallbacks used before React Flow's real DOM measurement
// takes over (dagre auto-layout, drag-snap gap math, team-box bounds) — kept
// proportionate to the actual rendered card size (nodes.tsx) so the single
// frame before measurement never estimates wildly off. Scaled +20% with the
// card size grant (Josh, print legibility, 2026-08-19); old: 160/80/64/64.
export const NODE_WIDTH = 192;
export const NODE_HEIGHT = 96;
export const NODE_WIDTH_COMPACT = 76;
export const NODE_HEIGHT_COMPACT = 76;
